import { useCallback, useEffect, useSyncExternalStore } from 'react'

export type ThemePref = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'meow.theme'

export function parseThemePref(raw: string | null | undefined): ThemePref {
  if (raw === 'light' || raw === 'dark' || raw === 'system') {
    return raw
  }
  return 'system'
}

export function readThemePref(): ThemePref {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 'system'
    return parseThemePref(window.localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return 'system'
  }
}

export function resolveTheme(pref: ThemePref, systemDark: boolean): ResolvedTheme {
  if (pref === 'system') {
    return systemDark ? 'dark' : 'light'
  }
  return pref
}

/**
 * The `theme-color` the browser paints behind the status bar. In the iOS home-screen app it fills the
 * status bar and a soft fade below it, so it must match what the page shows there: the `canvas`
 * color, or that color under the overlay scrim (rgb(20 20 18 / .4) light, rgb(0 0 0 / .55) dark)
 * while a drawer, dialog or bottom sheet is open — otherwise a light band hangs over the dimmed page.
 */
export function statusBarColor(resolved: ResolvedTheme, dimmed: boolean): string {
  if (resolved === 'dark') return dimmed ? '#0B0B0C' : '#18181A'
  return dimmed ? '#A1A1A0' : '#FFFFFF'
}

// Open overlays that dim the page (counted, since a sheet can open over a dialog).
let dimCount = 0

export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', statusBarColor(resolved, dimCount > 0))
  }
}

function getSystemDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

type Listener = () => void
const listeners = new Set<Listener>()

function notify(): void {
  for (const listener of listeners) {
    listener()
  }
}

let cachedPref: ThemePref = typeof window !== 'undefined' ? readThemePref() : 'system'

export function setThemePref(pref: ThemePref): void {
  cachedPref = pref
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(THEME_STORAGE_KEY, pref)
    }
  } catch {}
  const resolved = resolveTheme(pref, getSystemDark())
  applyTheme(resolved)
  notify()
}

function subscribe(callback: Listener): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

function getSnapshot(): ThemePref {
  return cachedPref
}

function getServerSnapshot(): ThemePref {
  return 'system'
}

function getResolvedSnapshot(): ResolvedTheme {
  return resolveTheme(cachedPref, getSystemDark())
}

function getServerResolvedSnapshot(): ResolvedTheme {
  return 'light'
}

let initialized = false
let mediaQueryList: MediaQueryList | null = null

export function initTheme(): () => void {
  if (typeof window === 'undefined') return () => {}

  cachedPref = readThemePref()
  const resolved = resolveTheme(cachedPref, getSystemDark())
  applyTheme(resolved)

  if (initialized) {
    return () => {}
  }
  initialized = true

  const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
    if (cachedPref === 'system') {
      applyTheme(resolveTheme('system', e.matches))
      notify()
    }
  }

  if (typeof window.matchMedia === 'function') {
    try {
      mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)')
      if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', handleMediaChange)
      } else if (typeof (mediaQueryList as any).addListener === 'function') {
        (mediaQueryList as any).addListener(handleMediaChange)
      }
    } catch {}
  }

  const handleStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY) {
      const next = parseThemePref(e.newValue)
      if (next !== cachedPref) {
        cachedPref = next
        applyTheme(resolveTheme(next, getSystemDark()))
        notify()
      }
    }
  }
  window.addEventListener('storage', handleStorage)

  return () => {
    if (mediaQueryList) {
      if (typeof mediaQueryList.removeEventListener === 'function') {
        mediaQueryList.removeEventListener('change', handleMediaChange)
      } else if (typeof (mediaQueryList as any).removeListener === 'function') {
        (mediaQueryList as any).removeListener(handleMediaChange)
      }
    }
    window.removeEventListener('storage', handleStorage)
  }
}

export function useThemePref(): [ThemePref, (pref: ThemePref) => void] {
  const pref = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const set = useCallback((p: ThemePref) => {
    setThemePref(p)
  }, [])
  return [pref, set]
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, getResolvedSnapshot, getServerResolvedSnapshot)
}

/**
 * Marks the page as dimmed by an overlay while `active` (a drawer, dialog or bottom sheet with a
 * scrim), so the status bar color follows the scrim. See `statusBarColor`.
 */
export function useStatusBarDim(active: boolean): void {
  useEffect(() => {
    if (!active) return
    dimCount += 1
    applyTheme(getResolvedSnapshot())
    return () => {
      dimCount -= 1
      applyTheme(getResolvedSnapshot())
    }
  }, [active])
}
